import { useState, useCallback, useEffect } from 'react';
import { debounce } from 'lodash';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// MUI
import { Button, Grid, TextField, FormControlLabel, Checkbox, Autocomplete, Box, Typography } from '@mui/material';

// Services
import { addOrUpdateCompanyRelations } from '../../../services/companies.services';
import { getStations } from '../../../services/station.service';

// Types
import { CompanyRelation } from '../../../types/Company';
import { FilterableTypes } from '../../../types/FilterableTypes';

interface Props {
  relation?: CompanyRelation | null;
  companyId: string;
  onSuccess: () => void;
  onCancel: () => void;
  onError: (message: string) => void;
}

const getValidationSchema = (isReaderContact: boolean) =>
  Yup.object({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    email: Yup.string().email('Invalid email').notRequired(),

    shippingAddress: Yup.object().shape({
      addressLine1: isReaderContact ? Yup.string().required('Address Line 1 is required') : Yup.string(),
      city: isReaderContact ? Yup.string().required('City is required') : Yup.string(),
      country: isReaderContact ? Yup.string().required('Country is required') : Yup.string(),
      addressLine2: Yup.string(),
      stateOrProvince: Yup.string(),
      postalCode: Yup.string(),
    }),
  });

const CompanyRelationForm = ({ relation, companyId, onSuccess, onCancel, onError }: Props) => {
  const [stationOptions, setStationOptions] = useState<{ id: string; iataCode: string }[]>([]);
  const [validationSchema, setValidationSchema] = useState(() => getValidationSchema(!!relation?.isReaderContact));

  const fetchStations = async (value?: string) => {
    try {
      const res = await getStations({
        skip: 0,
        take: 100,
        filters: value ? [{ iataCode: value, filterType: FilterableTypes.Contains }] : null,
      });
      if (res.data?.results) setStationOptions(res.data.results);
    } catch (err) {
      console.error('Error fetching stations', err);
    }
  };

  const debouncedFetchOptions = useCallback(debounce(fetchStations, 700), []);

  // Initialize Formik
  const formik = useFormik<Partial<CompanyRelation>>({
    initialValues: {
      id: relation?.id,
      companyId,
      firstName: relation?.firstName || '',
      lastName: relation?.lastName || '',
      fullName: relation?.fullName || '',
      initials: relation?.initials || '',
      email: relation?.email || '',
      phone: relation?.phone || '',
      jobDescription: relation?.jobDescription || '',
      stations: relation?.stations || [],
      isTransferContact: relation?.isTransferContact || false,
      isPrimaryTransferContact: relation?.isPrimaryTransferContact || false,
      isReaderContact: relation?.isReaderContact || false,
      isScanReportContact: relation?.isScanReportContact || false,
      shippingAddress: relation?.shippingAddress || {
        addressLine1: '',
        addressLine2: '',
        city: '',
        stateOrProvince: '',
        postalCode: '',
        country: '',
      },
    },
    validationSchema,

    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload: Partial<CompanyRelation> = {
          ...(relation?.id && { id: relation.id }),
          companyId,
          firstName: values.firstName,
          lastName: values.lastName,
          fullName: `${values.firstName} ${values.lastName}`,
          initials: values.firstName ? `${values.firstName[0]}.` : '',
          email: values.email,
          phone: values.phone || undefined,
          jobDescription: values.jobDescription || undefined,
          stations: values.stations?.map((s) => ({ id: s.id })) || [],
          isTransferContact: values.isTransferContact,
          isPrimaryTransferContact: values.isPrimaryTransferContact,
          isReaderContact: values.isReaderContact,
          isScanReportContact: values.isScanReportContact,
          shippingAddress: values.isReaderContact ? values.shippingAddress : undefined,
        };

        await addOrUpdateCompanyRelations(companyId, payload);
        onSuccess();
      } catch (err: any) {
        console.error('Error saving relation', err);
        // Handle 409 (Conflict)
        if (err.response?.status === 409 && err.response?.data?.message) {
          onError(err.response.data.message);
        } else {
          onError('An unexpected error occurred while saving the relation.');
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Preload station options if editing
  useEffect(() => {
    if (relation?.stations?.length) {
      const mapped = relation.stations.map((s) => ({
        id: s.id,
        iataCode: s.displayName || '',
      }));
      setStationOptions((prev) => {
        const newOptions = mapped.filter((s) => !prev.some((p) => p.id === s.id));
        return [...prev, ...newOptions];
      });
    }
  }, [relation]);

  useEffect(() => {
    if (!formik.values.isTransferContact && formik.values.isPrimaryTransferContact) {
      formik.setFieldValue('isPrimaryTransferContact', false);
    }
  }, [formik.values.isTransferContact]);

  useEffect(() => {
    setValidationSchema(getValidationSchema(!!formik.values.isReaderContact));
    formik.validateForm();
  }, [formik.values.isReaderContact]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={2}>
        {/* First Name / Last Name */}
        <Grid item xs={6} mt={4}>
          <TextField
            fullWidth
            id='firstName'
            name='firstName'
            label='First Name'
            value={formik.values.firstName}
            onChange={formik.handleChange}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
          />
        </Grid>
        <Grid item xs={6} mt={4}>
          <TextField
            fullWidth
            id='lastName'
            name='lastName'
            label='Last Name'
            value={formik.values.lastName}
            onChange={formik.handleChange}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={formik.touched.lastName && formik.errors.lastName}
          />
        </Grid>

        {/* Email / Phone */}
        <Grid item xs={6}>
          <TextField
            fullWidth
            id='email'
            name='email'
            label='Email'
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField fullWidth id='phone' name='phone' label='Phone' value={formik.values.phone} onChange={formik.handleChange} />
        </Grid>

        {/* Job Description / Station */}
        <Grid item xs={6}>
          <TextField
            fullWidth
            id='jobDescription'
            name='jobDescription'
            label='Job Description'
            value={formik.values.jobDescription}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            multiple
            options={stationOptions}
            getOptionLabel={(option) => option.iataCode}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={(formik.values.stations ?? []).map((s) => ({
              id: s.id,
              iataCode: s.displayName || '',
            }))}
            onChange={(_, newValue) => {
              const mapped = newValue.map((v) => ({
                id: v.id,
                displayName: v.iataCode,
              }));
              formik.setFieldValue('stations', mapped);
            }}
            onInputChange={(event, value, reason) => {
              if (reason === 'input' && value) debouncedFetchOptions(value);
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.iataCode}
              </li>
            )}
            renderInput={(params) => <TextField {...params} label='Stations' fullWidth />}
          />
        </Grid>

        {/* Checkboxes */}
        <Grid item xs={12}>
          {/* Transfer Contact */}
          <FormControlLabel
            control={<Checkbox checked={formik.values.isTransferContact || false} onChange={formik.handleChange} name='isTransferContact' />}
            label='Transfer Contact'
          />
          <FormControlLabel
            control={<Checkbox checked={formik.values.isReaderContact || false} onChange={formik.handleChange} name='isReaderContact' />}
            label='Reader Contact'
          />
          <FormControlLabel
            control={<Checkbox checked={formik.values.isScanReportContact || false} onChange={formik.handleChange} name='isScanReportContact' />}
            label='Scan Report Contact'
          />
        </Grid>
        {formik.values.isTransferContact && (
          <div style={{ marginLeft: '1rem' }}>
            <FormControlLabel
              control={
                <Checkbox checked={formik.values.isPrimaryTransferContact || false} onChange={formik.handleChange} name='isPrimaryTransferContact' />
              }
              label={<div style={{ width: '200px' }}>Is Primary Transfer Contact</div>}
            />
          </div>
        )}
        {formik.values.isReaderContact && (
          <Grid item xs={12}>
            <Box
              sx={{
                mt: 2,
                p: 2.5,
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
              }}
            >
              <Typography variant='subtitle2' sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
                📍 Shipping Address
              </Typography>

              <Grid container spacing={2}>
                {/* Address Line 1 */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id='shippingAddress.addressLine1'
                    name='shippingAddress.addressLine1'
                    label='Address Line 1'
                    placeholder='Street address'
                    value={formik.values.shippingAddress?.addressLine1 || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={(formik.touched.shippingAddress as any)?.addressLine1 && Boolean((formik.errors.shippingAddress as any)?.addressLine1)}
                    helperText={(formik.touched.shippingAddress as any)?.addressLine1 && (formik.errors.shippingAddress as any)?.addressLine1}
                  />
                </Grid>

                {/* Address Line 2 */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id='shippingAddress.addressLine2'
                    name='shippingAddress.addressLine2'
                    label='Address Line 2'
                    placeholder='Apartment, suite, floor, etc. (optional)'
                    value={formik.values.shippingAddress?.addressLine2 || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Grid>

                {/* City / State */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id='shippingAddress.city'
                    name='shippingAddress.city'
                    label='City'
                    value={formik.values.shippingAddress?.city || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={(formik.touched.shippingAddress as any)?.city && Boolean((formik.errors.shippingAddress as any)?.city)}
                    helperText={(formik.touched.shippingAddress as any)?.city && (formik.errors.shippingAddress as any)?.city}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id='shippingAddress.stateOrProvince'
                    name='shippingAddress.stateOrProvince'
                    label='State / Province'
                    value={formik.values.shippingAddress?.stateOrProvince || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      (formik.touched.shippingAddress as any)?.stateOrProvince && Boolean((formik.errors.shippingAddress as any)?.stateOrProvince)
                    }
                    helperText={(formik.touched.shippingAddress as any)?.stateOrProvince && (formik.errors.shippingAddress as any)?.stateOrProvince}
                  />
                </Grid>

                {/* Postal Code / Country */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id='shippingAddress.postalCode'
                    name='shippingAddress.postalCode'
                    label='Postal Code'
                    value={formik.values.shippingAddress?.postalCode || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id='shippingAddress.country'
                    name='shippingAddress.country'
                    label='Country'
                    value={formik.values.shippingAddress?.country || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={(formik.touched.shippingAddress as any)?.country && Boolean((formik.errors.shippingAddress as any)?.country)}
                    helperText={(formik.touched.shippingAddress as any)?.country && (formik.errors.shippingAddress as any)?.country}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        )}

        {/* Actions */}
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onCancel} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button type='submit' variant='contained' disabled={formik.isSubmitting}>
            {relation?.id ? 'Update Relation' : 'Add Relation'}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default CompanyRelationForm;
